<template>
  <div class="participant-select-wrapper">
    <FriedParagraph v-if="isSmallScreenAndDown && !open" @click="handleMobileTextClick">
      {{ mobileText }}
    </FriedParagraph>
    <template v-else>
      <FriedChip
        v-for="participant in addedParticipants"
        :key="getParticipantsDetails(participant).id"
        class="participant-chip"
        :label="getParticipantDisplayName(participant)"
        :is-removable="!isSmallScreenAndDown"
        @remove="removeParticipant(participant)"
      />
    </template>
    <SearchLayout
      v-show="showSearch"
      :open="open"
      thin-padding
      @open="setSearchOpen(true)"
      @close="setSearchOpen(false)"
    >
      <template #input>
        <FriedSearchInput
          v-model="searchInput"
          :placeholder="!addedParticipants.length ? t('search') : null"
          class="participant-search-box"
          @focus="$emit('focus')"
          @blur="$emit('blur')"
          @update:model-value="handleInput"
          @keyup.enter="enter"
          @keydown.up="handleArrowUp"
          @keydown.down="handleArrowDown"
          @keydown.backspace="handleBackspace"
        >
        </FriedSearchInput>
      </template>
      <template v-if="participantSuggestions.length" #content>
        <div class="suggestions">
          <FriedDropdownItem
            v-for="(participant, index) in participantSuggestions"
            :key="getParticipantsDetails(participant).id"
            :class="{ focused: index === focusedIndex }"
            @click="handleSelect(participant)"
          >
            <div class="participant-item">
              <FriedAvatar
                :name="getParticipantDisplayName(participant)"
                :thumb-url="getParticipantsDetails(participant).thumbUrl"
                :size="AvatarSize.Small"
              />
              <div class="participant-details">
                <FriedParagraph
                  >{{ getParticipantDisplayName(participant)
                  }}<span v-if="getParticipantsDetails(participant).title"
                    >, {{ getParticipantsDetails(participant).title }}
                  </span></FriedParagraph
                >
                <FriedParagraph class="participant-company" :size="ParagraphSize.Small">
                  {{ getParticipantCompanyName(participant) }}
                </FriedParagraph>
              </div>
            </div>
          </FriedDropdownItem>
        </div>
      </template>
      <template v-else #content>
        <FriedParagraph>{{ t('no-search-results') }}</FriedParagraph>
      </template>
    </SearchLayout>
  </div>
</template>

<script lang="ts">
import { IconSize, ParagraphSize, ButtonType, AvatarSize } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { nextTick, onMounted, ref, defineComponent, computed, watch } from 'vue';
import SearchLayout from '@getaccept/lib-shared-new/src/search/components/SearchLayout.vue';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import { useI18n } from 'vue-i18n';
import type { ChatParticipant, OpenConversationPayload } from '../types';
import { ChatHelper } from '../helpers';

export default defineComponent({
  components: {
    SearchLayout,
  },
  props: {
    participants: {
      type: Array as PropType<ChatParticipant[]>,
      default: () => [],
    },
    openConversationPayload: {
      type: Object as PropType<OpenConversationPayload>,
      default: null,
    },
    ownerCompanyName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const updateParticipantsEmit = 'update-participants';
    const searchInput = ref('');

    const open = ref(false);
    const focusedIndex = ref(-1);

    const addedParticipants = ref<ChatParticipant[]>([]);
    const participantSuggestions = ref<ChatParticipant[]>();

    const matchSearchInput = (chatParticipant: ChatParticipant, searchRegex: RegExp) => {
      const { user, participant } = chatParticipant;
      return [user?.fullName, user?.email, participant?.fullName, participant?.email].some(
        value => value && searchRegex.test(value)
      );
    };

    const isAdded = (participant: ChatParticipant) => addedParticipants.value.includes(participant);

    const handleInput = () => {
      const searchRegex = new RegExp(`(\\W|^)${searchInput.value}`, 'i');

      participantSuggestions.value = props.participants.filter(
        participant => matchSearchInput(participant, searchRegex) && !isAdded(participant)
      );
    };

    watch(
      () => props.participants,
      (newValue, oldValue) => {
        if (newValue?.length !== oldValue?.length) {
          participantSuggestions.value = newValue;
          handleInput();
        }
      },
      { immediate: true }
    );

    const handleClear = async () => {
      focusedIndex.value = -1;
      searchInput.value = '';
      handleFocus();
      await nextTick();
      setSearchOpen(true);
    };

    const handleSelect = (participant: ChatParticipant) => {
      if (participant) {
        addedParticipants.value.push(participant);
        handleUpdateParticipants();
      }
    };

    const { isSmallScreenAndDown } = useBreakpoints();

    const mobileText = computed(() => {
      const participantNames = addedParticipants.value.map(p => getParticipantDisplayName(p));
      const [first, second, ...others] = participantNames;

      if (!first) return '';
      if (!second) return first;
      if (!others.length) return `${first}, ${second}`;

      return `${first}, ${second} & ${others.length} ${t('dr_chat_more')}`;
    });

    const setSearchOpen = (show: boolean) => {
      open.value = show;
    };

    const removeParticipant = (participant: ChatParticipant) => {
      addedParticipants.value = addedParticipants.value.filter(
        p => p.userId !== participant.userId || p.participantId !== participant.participantId
      );
      emit(updateParticipantsEmit, addedParticipants.value);
      handleInput();
    };

    const enter = () => {
      if (!open.value) {
        setSearchOpen(true);
        return;
      }
      handleSelect(participantSuggestions.value[focusedIndex.value]);
    };

    const handleArrowUp = () => {
      if (focusedIndex.value > 0) {
        focusedIndex.value -= 1;
      }
    };

    const handleArrowDown = () => {
      if (focusedIndex.value < participantSuggestions.value.length - 1) {
        focusedIndex.value += 1;
      }
    };

    const handleUpdateParticipants = () => {
      setSearchOpen(false);
      handleClear();
      handleInput();
      emit(updateParticipantsEmit, addedParticipants.value);
    };

    const handleBackspace = () => {
      if (!searchInput.value && addedParticipants.value.length) {
        addedParticipants.value.pop();
        handleUpdateParticipants();
      }
    };

    const handleFocus = () => {
      const searchBoxInput: HTMLInputElement | null = document.querySelector(
        '.participant-search-box > input'
      );
      if (searchBoxInput) {
        searchBoxInput?.focus();
      }
    };

    const getParticipantCompanyName = (chatParticipant: ChatParticipant) => {
      if (chatParticipant.userId) {
        return props.ownerCompanyName;
      }
      return chatParticipant.participant.companyName;
    };

    const handleMobileTextClick = async () => {
      setSearchOpen(false);
      handleClear();
      handleInput();
      await nextTick();
      handleFocus();
      setSearchOpen(true);
    };

    const showSearch = computed(
      () => !isSmallScreenAndDown.value || open.value || !addedParticipants.value.length
    );

    const getParticipantDisplayName = (participant: ChatParticipant) => {
      const { fullName, email } = ChatHelper.getParticipantsDetails(participant);
      return fullName || email;
    };

    onMounted(async () => {
      if (
        props.openConversationPayload?.participantId ||
        props.openConversationPayload?.conversationId
      ) {
        const chatParticipantId = props.openConversationPayload.participantId;

        addedParticipants.value = props.participants.filter(
          p => p.participantId === chatParticipantId || p.userId === chatParticipantId
        );
        emit(updateParticipantsEmit, addedParticipants.value);
        return;
      }

      setSearchOpen(true);
      await nextTick();
      handleFocus();
    });

    return {
      open,
      searchInput,
      handleArrowUp,
      handleArrowDown,
      focusedIndex,
      handleBackspace,

      getParticipantCompanyName,
      enter,
      handleSelect,
      setSearchOpen,
      handleClear,
      handleInput,
      removeParticipant,
      addedParticipants,
      participantSuggestions,
      handleUpdateParticipants,

      getParticipantsDetails: ChatHelper.getParticipantsDetails,
      ParagraphSize,
      IconSize,
      ButtonType,
      AvatarSize,
      mobileText,
      t,
      isSmallScreenAndDown,
      handleMobileTextClick,
      showSearch,
      getParticipantDisplayName,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/lib-shared-new/src/assets/scss/search-bar';
@import '@getaccept/dsr-shared-new/src/scss/chat-view';

$search-box-width: 350px;

.focused {
  background-color: var(--gray-96);
}

.participant-search-box {
  width: $search-box-width;
  max-width: 100%;

  @media screen and (max-width: $xs) {
    width: unset;
  }
}

.participant-company {
  color: var(--gray-39);
}
</style>
